import { BookOpen, CircleHelp, FileText, Mail, Sparkles } from "lucide-react";
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { useHelp } from "@/components/layout/HelpProvider";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { HELP_MENU_FAQ_IDS, HELP_SUPPORT_EMAIL, helpItemQuestionKey } from "@/content/helpFaq";

export function HelpMenuItems(): ReactElement {
  const { t } = useTranslation();
  const { openChat } = useHelp();

  return (
    <>
      <DropdownMenuLabel>{t("help.learnMore")}</DropdownMenuLabel>
      <DropdownMenuGroup>
        {HELP_MENU_FAQ_IDS.map((id) => (
          <DropdownMenuItem key={id} onClick={() => openChat({ faqId: id })}>
            <FileText className="size-4" aria-hidden />
            <span className="min-w-0 truncate">{t(helpItemQuestionKey(id))}</span>
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem onClick={() => openChat({ browseAll: true })}>
          <BookOpen className="size-4" aria-hidden />
          <span>{t("help.helpCenter")}</span>
        </DropdownMenuItem>
      </DropdownMenuGroup>
      <DropdownMenuSeparator />
      <DropdownMenuLabel>{t("help.contactUs")}</DropdownMenuLabel>
      <DropdownMenuGroup>
        <DropdownMenuItem onClick={() => openChat()}>
          <Sparkles className="size-4" aria-hidden />
          <span>{t("help.askAssistant")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <a href={`mailto:${HELP_SUPPORT_EMAIL}`}>
            <Mail className="size-4" aria-hidden />
            <span>{t("help.emailSupport")}</span>
          </a>
        </DropdownMenuItem>
      </DropdownMenuGroup>
    </>
  );
}

export function HelpShortcutButton(): ReactElement {
  const { t } = useTranslation();
  const label = t("help.title");

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-9 shrink-0 lg:size-12"
              aria-label={label}
            >
              <CircleHelp className="size-[22px]" aria-hidden />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">{label}</TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-80">
        <HelpMenuItems />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
